import React, { useState, forwardRef, useImperativeHandle } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { createGlucoseLevelReading } from "../../../../services/patient/details";
import DatePicker from "../../../shared-modules/DatePicker/DatePicker";
import useCurrentTime from "../../../shared-modules/DatePicker/useCurrentTime";
import CustomInput from "../../../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import styles from "../../../../styles/patient-page-modules/addReadingDialog.module.css";

const eventEnum = ["after_meal", "before_meal"];

const schemaValidation = z.object({
  glucose: z.coerce
    .number()
    .positive("glucose must be a positive value")
    .min(1, "glucose level is required.")
    .max(999, "invalid glucose value"),
  event: z.string().refine((val) => eventEnum.includes(val), {
    message: "Invalid event",
  }),
  recorded_at: z.coerce.date(),
});

/**
 * GlucoseForm - Form for adding blood glucose readings
 * Extends the existing AddGlucoseReading with new layout and chrome
 *
 * @param {Object} props
 * @param {React.ComponentType} props.icon - The reading type icon component
 * @param {string} props.title - The title for this reading type
 * @param {Function} props.onSuccess - Callback when form is successfully submitted
 * @param {Function} props.onLoadingChange - Callback when loading state changes
 */
const GlucoseForm = forwardRef(({ icon: Icon, title, onSuccess, onLoadingChange }, ref) => {
  const curr = useCurrentTime();
  const [selectedDate, setSelectedDate] = useState(null);
  const queryClient = useQueryClient();
  const [patientId] = useState(() => sessionStorage.getItem("_pt_id"));

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm({
    resolver: zodResolver(schemaValidation),
    defaultValues: {
      glucose: null,
      event: null,
      recorded_at: null,
    },
  });

  const eventValue = watch("event");

  const mutation = useMutation(
    (newReading) => createGlucoseLevelReading(newReading),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["patient-readings"]);
        toast.success("Reading added successfully");
        setSelectedDate(null);
        reset();
        if (onSuccess) onSuccess();
      },
      onError: () => {
        toast.error("Could not add reading");
      },
      onSettled: () => {
        if (onLoadingChange) onLoadingChange(false);
      },
    }
  );

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleCancelSelect = () => {
    setSelectedDate(null);
  };

  const formatToLabels = (badLabel) => {
    const labels = badLabel.split("_");
    return labels.join(" ");
  };

  const submit = (values) => {
    if (onLoadingChange) onLoadingChange(true);
    values.patient = patientId;
    values.recorded_at = selectedDate;
    values.reading_type = "blood_glucose";
    mutation.mutate(values);
  };

  // Expose submit method to parent via ref
  useImperativeHandle(ref, () => ({
    submit: () => {
      handleSubmit(submit)();
    },
  }));

  return (
    <form onSubmit={handleSubmit(submit)} className={styles.formContainer}>
      {/* Form Chrome - Icon + Title + Subtitle */}
      <div className={styles.formChrome}>
        <div className={styles.readingIcon}>
          <Icon />
        </div>
        <div className={styles.formTitleSection}>
          <h3 className={styles.formTitle}>{title}</h3>
          <p className={styles.formSubtitle}>Add a reading for {title.toLowerCase()}</p>
        </div>
      </div>

      {/* Form Body - 2-column grid */}
      <div className={styles.formGrid}>
        {/* Row 1: Glucose | Select Event */}
        <CustomInput
          {...register("glucose")}
          type="number"
          placeholder="Glucose"
          autoFocus
          error={errors?.glucose?.message}
        />

        <div className={styles.selectWrapper}>
          <Select
            value={eventValue || ""}
            onChange={(value) => setValue("event", value)}
            placeholder="Select Event"
            className={styles.eventSelect}
          >
            <SelectTrigger className={styles.selectTrigger}>
              <SelectValue placeholder="Select Event" />
            </SelectTrigger>
            <SelectContent>
              {eventEnum.map((event) => (
                <SelectItem key={event} value={event}>
                  {formatToLabels(event)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors?.event && (
            <p className={styles.errorMessage}>{errors.event.message}</p>
          )}
        </div>

        {/* Row 2: Date picker | (empty cell) */}
        <div className={styles.dateWrapper}>
          <div className={styles.datePickerWrapper}>
            <DatePicker
              selectedDate={selectedDate || curr}
              onDateSelect={handleDateChange}
              showTime={true}
              onCancelSelect={handleCancelSelect}
            />
          </div>
          {errors?.recorded_at && (
            <p className={styles.errorMessage}>{errors.recorded_at.message}</p>
          )}
        </div>

        {/* Empty cell for grid alignment */}
        <div></div>
      </div>
    </form>
  );
});

GlucoseForm.displayName = "GlucoseForm";

export default GlucoseForm;
